/**
 * 3D representation of a single drill role player. Reuses Josh's
 * ProceduralFootballer so drill 3D figures look identical to the
 * tactics 3D figures — same materials, same HDRI response, same
 * cinematic feel. Just with the role's brand colour instead of
 * home/away green/amber.
 */
import { forwardRef, useMemo } from 'react';
import { Text, Billboard } from '@react-three/drei';
import * as THREE from 'three';
import { roleForId, type DrillRolePlayer } from '../playerRoles';
import { ProceduralFootballer } from '../../tactics3d/ProceduralFootballer';

interface Props {
  player: DrillRolePlayer;
  /** World position in metres (drill-norm already converted upstream). */
  worldPosition: { x: number; z: number };
}

/** Figure scale — Josh's tactics uses ~1.0. Drills use a slightly
 *  smaller scale so 8 players fit on a 15m grid without crowding. */
const FIGURE_SCALE = 0.85;
/** Ball is a small white sphere — smaller than Josh's BallMesh (0.3m)
 *  to match the 2D 0.32× token scale. */
const BALL_RADIUS = 0.22;

export const DrillRolePlayer3D = forwardRef<THREE.Group, Props>(function DrillRolePlayer3D(
  { player, worldPosition },
  ref,
) {
  const role = roleForId(player.roleId);
  const color = player.color ?? role.color;
  const isBall = role.id === 'ball';

  // Stable per-token idle phase so a row of players don't bob in unison.
  const idlePhase = useMemo(() => {
    let h = 0;
    for (let i = 0; i < player.id.length; i += 1) h = (h * 31 + player.id.charCodeAt(i)) | 0;
    return (Math.abs(h) % 1000) / 1000 * Math.PI * 2;
  }, [player.id]);

  if (isBall) {
    const ballMat = new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.4, metalness: 0.05 });
    return (
      <group ref={ref} position={[worldPosition.x, BALL_RADIUS, worldPosition.z]} userData={{ kind: 'ball', playerId: player.id }}>
        <mesh material={ballMat} castShadow>
          <sphereGeometry args={[BALL_RADIUS, 24, 16]} />
        </mesh>
        <mesh position={[0, -BALL_RADIUS + 0.005, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
          <circleGeometry args={[BALL_RADIUS * 1.2, 16]} />
          <meshBasicMaterial color="black" transparent opacity={0.35} />
        </mesh>
      </group>
    );
  }

  return (
    <group
      ref={ref}
      position={[worldPosition.x, 0, worldPosition.z]}
      scale={FIGURE_SCALE}
      userData={{ kind: 'rolePlayer', roleId: player.roleId, playerId: player.id, label: player.label ?? '' }}
    >
      {/* Drop shadow under feet. */}
      <mesh position={[0, 0.005, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <circleGeometry args={[0.75, 16]} />
        <meshBasicMaterial color="black" transparent opacity={0.4} />
      </mesh>
      {/* Josh's ProceduralFootballer — same humanoid the tactics board
          uses, tinted by the player's role colour. */}
      <ProceduralFootballer
        teamHex={color}
        selected={false}
        idlePhase={idlePhase}
        reducedMotion={false}
      />
      {/* Billboarded label floating above the figure. */}
      <Billboard position={[0, 2.45, 0]}>
        <Text
          fontSize={0.6}
          color="#ffffff"
          outlineWidth={0.06}
          outlineColor="#000000"
          outlineOpacity={0.9}
          anchorX="center"
          anchorY="middle"
        >
          {player.label ?? role.glyph ?? ''}
        </Text>
      </Billboard>
    </group>
  );
});
